import { CSSProperties, useEffect, useRef, useState } from 'react';
import { ChevronRight, CursorFill, ExclamationTriangleFill, PlusLg, Search, Trash3 } from 'react-bootstrap-icons';
import { GetQuestionsRequest, GetQuestionsResponse, Question } from '../protos/backendproto/QuestionService_pb';
import { AdminQuestionServiceClient } from '../protos/adminbackendproto/AdminQuestionServiceServiceClientPb';
import ConfirmationDialog from './ConfirmationDialog';
import QuestionCard from './QuestionCard';
import QuestionEditorPane from './QuestionEditor';
import { GetHasEditAccess, GetServiceClient } from '../App';

/** A room (interior) or area (exterior/basement): a named list of questions. */
export interface QuestionGroup {
    getQuestionsList(): Question[];
    setQuestionsList(value: Question[]): unknown;
}

/** Everything that differs between the interior, exterior and basement pages. */
export interface GroupAdapter<G extends QuestionGroup> {
    title: string;
    noun: string;
    groups: (response: GetQuestionsResponse) => G[];
    name: (group: G) => string;
    setName: (group: G, name: string) => void;
    create: () => G;
    save: (client: AdminQuestionServiceClient, groups: G[]) => Promise<unknown>;
}

type Selection = { groupIdx: number, questionIdx: number, question: Question }
type SaveState = 'idle' | 'saving' | 'saved' | 'error'

const cloneResponse = (r: GetQuestionsResponse) => GetQuestionsResponse.deserializeBinary(r.serializeBinary())
const cloneQuestion = (q: Question) => Question.deserializeBinary(q.serializeBinary())

// Keeps a set of group indices pointing at the same groups after an insert
// (delta 1) or removal (delta -1) at `at`.
function shiftIndices(set: Set<number>, at: number, delta: 1 | -1): Set<number> {
    const next = new Set<number>()
    set.forEach((i) => {
        if (delta === 1) next.add(i >= at ? i + 1 : i)
        else if (i !== at) next.add(i > at ? i - 1 : i)
    })
    return next
}

function questionMatches(question: Question, query: string) {
    return [question.getPrompt(), question.getId(), question.getHint(), ...question.getChoicesList().map((c) => c.getMessage())]
        .some((text) => text.toLowerCase().includes(query))
}

export default function QuestionsWorkspace<G extends QuestionGroup>({ adapter }: { adapter: GroupAdapter<G> }) {
    const client = GetServiceClient()
    const hasEditAccess = GetHasEditAccess()

    const [questions, setQuestions] = useState<GetQuestionsResponse | null>(null)
    const [loadError, setLoadError] = useState<string>()
    const [selected, setSelected] = useState<Selection | null>(null)
    const [needsSave, setNeedsSave] = useState(false)
    // Bumped to remount the editor (new selection, or discarded edits).
    const [editorEpoch, setEditorEpoch] = useState(0)
    const [listDirty, setListDirty] = useState(false)
    const [saveState, setSaveState] = useState<SaveState>('idle')
    const [lastSavedAt, setLastSavedAt] = useState<Date>()
    const [expanded, setExpanded] = useState<Set<number>>(new Set())
    const [query, setQuery] = useState("")
    const [focusGroupIdx, setFocusGroupIdx] = useState<number>()
    const [toast, setToast] = useState<{ message: string, key: number }>()
    const editorPaneRef = useRef<HTMLDivElement>(null)
    // Incremented on every list edit so a save only clears the dirty flag if
    // nothing changed while it was in flight.
    const editVersion = useRef(0)

    useEffect(() => {
        client.getAllQuestions(new GetQuestionsRequest(), {}).then((response) => {
            setQuestions(response)
        }).catch((err) => {
            console.log("Failed to load questions", err)
            setLoadError("Couldn't load questions. Check the backend and refresh.")
        })
    }, [])

    useEffect(() => {
        if (!toast) return
        const timer = setTimeout(() => setToast(undefined), 2600)
        return () => clearTimeout(timer)
    }, [toast])

    const isDirty = needsSave || listDirty
    useEffect(() => {
        if (!isDirty) return
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = "" }
        window.addEventListener("beforeunload", handler)
        return () => window.removeEventListener("beforeunload", handler)
    }, [isDirty])

    // ⌘S / Ctrl+S: save the open question if it has edits, otherwise the list.
    const shortcutRef = useRef<() => void>()
    shortcutRef.current = () => saveAll()
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() === "s" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                shortcutRef.current?.()
            }
        }
        document.addEventListener("keydown", handler)
        return () => document.removeEventListener("keydown", handler)
    }, [])

    function showToast(message: string) {
        setToast({ message, key: Date.now() })
    }

    function persist(response: GetQuestionsResponse) {
        const version = editVersion.current
        setSaveState('saving')
        adapter.save(client, adapter.groups(response)).then(() => {
            setSaveState('saved')
            setLastSavedAt(new Date())
            if (editVersion.current === version) setListDirty(false)
        }).catch((err) => {
            console.log("Failed to save questions", err)
            setSaveState('error')
        })
    }

    // Applies an edit to a copy of the whole response and marks the list dirty.
    function mutate(mutation: (groups: G[]) => void): GetQuestionsResponse {
        const edited = cloneResponse(questions!)
        mutation(adapter.groups(edited))
        editVersion.current += 1
        setQuestions(edited)
        setListDirty(true)
        return edited
    }

    function commitSelected() {
        if (!selected || !questions) return
        const edited = cloneResponse(questions)
        const questionsList = adapter.groups(edited)[selected.groupIdx].getQuestionsList()
        const oldId = questionsList[selected.questionIdx].getId()
        const newId = selected.question.getId()
        questionsList[selected.questionIdx] = cloneQuestion(selected.question)
        if (oldId !== newId) {
            questionsList.forEach((q) => {
                if (q.hasDependentQuestionId() && q.getDependentQuestionId() === oldId) {
                    console.log("Updating dependent question ID for question: " + q.getId() + " because ID has changed from " + oldId + " to " + newId)
                    q.setDependentQuestionId(newId)
                }
            })
        }
        editVersion.current += 1
        setQuestions(edited)
        setNeedsSave(false)
        persist(edited)
    }

    function saveAll() {
        if (!hasEditAccess || !questions || saveState === 'saving') return
        if (selected && needsSave) {
            commitSelected()
        } else {
            persist(questions)
        }
    }

    async function confirmDiscardIfNeeded() {
        if (!needsSave) return true
        return await ConfirmationDialog("Discard changes?", "The open question has unsaved edits. Discard them?", { okLabel: "Discard" })
    }

    function openQuestion(groupIdx: number, questionIdx: number, from: GetQuestionsResponse) {
        const question = adapter.groups(from)[groupIdx].getQuestionsList()[questionIdx]
        setSelected({ groupIdx, questionIdx, question: cloneQuestion(question) })
        setNeedsSave(false)
        setEditorEpoch((e) => e + 1)
        editorPaneRef.current?.scrollTo({ top: 0 })
    }

    async function selectQuestion(groupIdx: number, questionIdx: number) {
        if (selected?.groupIdx === groupIdx && selected.questionIdx === questionIdx) return
        if (!(await confirmDiscardIfNeeded())) return
        openQuestion(groupIdx, questionIdx, questions!)
    }

    async function addQuestion(groupIdx: number) {
        if (!(await confirmDiscardIfNeeded())) return
        const insertAt = selected?.groupIdx === groupIdx
            ? selected.questionIdx + 1
            : adapter.groups(questions!)[groupIdx].getQuestionsList().length
        const edited = mutate((groups) => {
            groups[groupIdx].getQuestionsList().splice(insertAt, 0, new Question())
        })
        setExpanded((current) => new Set(current).add(groupIdx))
        openQuestion(groupIdx, insertAt, edited)
    }

    async function deleteQuestion(groupIdx: number, questionIdx: number) {
        const prompt = adapter.groups(questions!)[groupIdx].getQuestionsList()[questionIdx].getPrompt()
        if (!(await ConfirmationDialog("Delete question", `Delete “${prompt || "Untitled question"}”? It's removed for good once you save.`))) return
        mutate((groups) => {
            groups[groupIdx].getQuestionsList().splice(questionIdx, 1)
        })
        if (selected?.groupIdx === groupIdx) {
            if (selected.questionIdx === questionIdx) {
                setSelected(null)
                setNeedsSave(false)
            } else if (questionIdx < selected.questionIdx) {
                setSelected({ ...selected, questionIdx: selected.questionIdx - 1 })
            }
        }
    }

    function addGroup() {
        const at = selected ? selected.groupIdx : 0
        mutate((groups) => {
            groups.splice(at, 0, adapter.create())
        })
        setExpanded((current) => shiftIndices(current, at, 1).add(at))
        if (selected) setSelected({ ...selected, groupIdx: selected.groupIdx + 1 })
        setQuery("")
        setFocusGroupIdx(at)
    }

    async function deleteGroup(groupIdx: number) {
        const name = adapter.name(adapter.groups(questions!)[groupIdx])
        if (!(await ConfirmationDialog(`Delete ${adapter.noun}`, `Are you sure you want to delete ${name || "this " + adapter.noun} and all of its questions?`))) return
        mutate((groups) => {
            groups.splice(groupIdx, 1)
        })
        setExpanded((current) => shiftIndices(current, groupIdx, -1))
        if (selected?.groupIdx === groupIdx) {
            setSelected(null)
            setNeedsSave(false)
        } else if (selected && groupIdx < selected.groupIdx) {
            setSelected({ ...selected, groupIdx: selected.groupIdx - 1 })
        }
    }

    function moveSelected(delta: 1 | -1) {
        if (!selected) return
        const count = adapter.groups(questions!)[selected.groupIdx].getQuestionsList().length
        const newIdx = selected.questionIdx + delta
        if (newIdx < 0 || newIdx >= count) return
        mutate((groups) => {
            const group = groups[selected.groupIdx]
            const questionsList = group.getQuestionsList()
            questionsList.splice(newIdx, 0, questionsList.splice(selected.questionIdx, 1)[0])
            group.setQuestionsList(questionsList)
        })
        setSelected({ ...selected, questionIdx: newIdx })
    }

    function copySelectedTo(groupIdx: number) {
        if (!selected || !questions) return
        const question = cloneQuestion(selected.question)
        const edited = cloneResponse(questions)
        const target = adapter.groups(edited)[groupIdx]
        const targetQuestions = target.getQuestionsList()
        const dependentQuestionExistsInNew = targetQuestions.some((other) => other.getId() === question.getDependentQuestionId())
        if (!dependentQuestionExistsInNew) {
            question.clearDependentQuestionId()
            question.clearDependentQuestionChoiceIdsList()
        }
        if (targetQuestions.some((other) => other.getId() === question.getId())) {
            question.setId(question.getId() + "_COPY")
        }
        targetQuestions.push(question)
        editVersion.current += 1
        setQuestions(edited)
        setExpanded((current) => new Set(current).add(groupIdx))
        persist(edited)
        showToast(`Copied to ${adapter.name(target) || "untitled " + adapter.noun}`)
    }

    function toggleGroup(groupIdx: number) {
        setExpanded((current) => {
            const next = new Set(current)
            if (next.has(groupIdx)) next.delete(groupIdx)
            else next.add(groupIdx)
            return next
        })
    }

    if (loadError) {
        return <div className="page">
            <div className="p-4"><div className="banner danger"><ExclamationTriangleFill className="mt-1" /> {loadError}</div></div>
        </div>
    }

    if (questions == null) {
        return <div className="page">
            <header className="page-header"><div>
                <h1 className="page-title">{adapter.title}</h1>
                <div className="skeleton mt-2" style={{ width: 140, height: 12 }} />
            </div></header>
            <div className="p-3 d-flex flex-column gap-2" style={{ maxWidth: 420 }}>
                {Array.from({ length: 7 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 46, opacity: 1 - i * 0.12 }} />)}
            </div>
        </div>
    }

    const groups = adapter.groups(questions)
    const totalQuestions = groups.reduce((sum, g) => sum + g.getQuestionsList().length, 0)
    const normalizedQuery = query.trim().toLowerCase()
    const visibleGroups = groups.map((group, groupIdx) => {
        const questionsList = group.getQuestionsList()
        const nameMatches = !normalizedQuery || adapter.name(group).toLowerCase().includes(normalizedQuery)
        const questionIdxs = questionsList.map((_, i) => i)
            .filter((i) => nameMatches || questionMatches(questionsList[i], normalizedQuery))
        return { group, groupIdx, questionIdxs, visible: nameMatches || questionIdxs.length > 0 }
    }).filter((g) => g.visible)
    const selectedGroup = selected ? groups[selected.groupIdx] : undefined

    return <div className="page">
        <header className="page-header">
            <div className="flex-grow-1">
                <h1 className="page-title">{adapter.title}</h1>
                <div className="page-subtitle">{groups.length} {adapter.noun}s · {totalQuestions} questions</div>
            </div>
            <SaveStatus state={saveState} isDirty={isDirty} lastSavedAt={lastSavedAt} />
            {hasEditAccess && <button className={"btn " + (isDirty ? "btn-accent" : "btn-ghost")} disabled={saveState === 'saving'} onClick={saveAll}>
                Save all <span className={"kbd" + (isDirty ? " kbd-on-accent" : "")}>⌘S</span>
            </button>}
        </header>

        <div className="split">
            <div className="pane list-pane">
                <div className="list-toolbar">
                    <div className="search flex-grow-1">
                        <Search size={13} />
                        <input className="form-control" placeholder={`Search ${adapter.noun}s and questions`} value={query} onChange={(e) => setQuery(e.target.value)} />
                    </div>
                    {hasEditAccess && <button className="btn btn-ghost" onClick={addGroup} title={`Add ${adapter.noun}`}>
                        <PlusLg /> {adapter.noun[0].toUpperCase() + adapter.noun.slice(1)}
                    </button>}
                </div>

                <div className="group-list stagger">
                    {visibleGroups.map(({ group, groupIdx, questionIdxs }, n) => {
                        const isOpen = !!normalizedQuery || expanded.has(groupIdx)
                        const questionsList = group.getQuestionsList()
                        return <section key={groupIdx} className={"group" + (isOpen ? " open" : "")} style={{ "--i": n } as CSSProperties}>
                            <div className="group-header" onClick={() => toggleGroup(groupIdx)}>
                                <ChevronRight className="chevron" size={12} />
                                <input
                                    className="group-name"
                                    value={adapter.name(group)}
                                    placeholder={`Untitled ${adapter.noun}`}
                                    disabled={!hasEditAccess}
                                    autoFocus={focusGroupIdx === groupIdx}
                                    onFocus={() => setFocusGroupIdx(undefined)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => mutate((gs) => adapter.setName(gs[groupIdx], e.target.value))} />
                                <span className="count">{questionsList.length}</span>
                                {hasEditAccess && <button className="icon-btn danger hover-reveal" title={`Delete ${adapter.noun}`} onClick={(e) => {
                                    e.stopPropagation()
                                    deleteGroup(groupIdx)
                                }}><Trash3 size={13} /></button>}
                            </div>
                            <div className="collapse-grid">
                                <div className="collapse-inner">
                                    <div className="group-body">
                                        {questionIdxs.map((questionIdx) => {
                                            const isSelected = selected?.groupIdx === groupIdx && selected.questionIdx === questionIdx
                                            // The open question shows its unsaved edits in the list too.
                                            const question = isSelected ? selected!.question : questionsList[questionIdx]
                                            return <QuestionCard key={questionIdx} hasEditAccess={hasEditAccess} question={question} index={questionIdx}
                                                isSelected={isSelected}
                                                onSelect={() => selectQuestion(groupIdx, questionIdx)}
                                                onDelete={() => deleteQuestion(groupIdx, questionIdx)} />
                                        })}
                                        {hasEditAccess && !normalizedQuery && <button className="add-question" onClick={() => addQuestion(groupIdx)}>
                                            <PlusLg size={12} /> Add question
                                        </button>}
                                    </div>
                                </div>
                            </div>
                        </section>
                    })}
                    {visibleGroups.length === 0 && <div className="empty-state py-5">
                        <div className="empty-icon"><Search /></div>
                        <h3>No matches</h3>
                        <div>Nothing matches “{query}”.</div>
                    </div>}
                </div>
            </div>

            <div className="pane editor-pane" ref={editorPaneRef}>
                {selected && selectedGroup
                    ? <QuestionEditorPane
                        key={`${selected.groupIdx}-${editorEpoch}`}
                        hasEditAccess={hasEditAccess}
                        question={selected.question}
                        allQuestions={selectedGroup.getQuestionsList()}
                        groupName={adapter.name(selectedGroup)}
                        position={{ index: selected.questionIdx, count: selectedGroup.getQuestionsList().length }}
                        copyTargets={groups.map((g) => adapter.name(g))}
                        needsSave={needsSave}
                        isSaving={saveState === 'saving'}
                        updateQuestion={(question: Question) => {
                            setSelected({ ...selected, question })
                            setNeedsSave(true)
                        }}
                        saveChanges={commitSelected}
                        discardChanges={() => openQuestion(selected.groupIdx, selected.questionIdx, questions)}
                        copyQuestionTo={copySelectedTo}
                        moveQuestionIdxUp={() => moveSelected(-1)}
                        moveQuestionIdxDown={() => moveSelected(1)} />
                    : <div className="empty-state">
                        <div className="empty-icon"><CursorFill /></div>
                        <h3>Pick a question</h3>
                        <div>Open a {adapter.noun} on the left and select a question to edit it.</div>
                    </div>}
            </div>
        </div>

        {toast && <div key={toast.key} className="toast-pop">{toast.message}</div>}
    </div>
}

function SaveStatus({ state, isDirty, lastSavedAt }: { state: SaveState, isDirty: boolean, lastSavedAt?: Date }) {
    if (state === 'saving') {
        return <span className="status-pill"><span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10, borderWidth: 1.5 }} /> Saving…</span>
    }
    if (state === 'error') {
        return <span className="status-pill text-danger"><span className="status-dot danger" /> Save failed, try again</span>
    }
    if (isDirty) {
        return <span className="status-pill"><span className="status-dot warn" /> Unsaved changes</span>
    }
    if (lastSavedAt) {
        return <span className="status-pill"><span className="status-dot" /> Saved {lastSavedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
    }
    return null
}
